/**
 * Notifications tRPC seam — SINGLE cast point.
 *
 * The `notifications` router is landing in a parallel backend PR
 * (`notifications.list` / `unreadCount` / `markRead` / `markAllRead` /
 * `dismiss` / `dismissAll`). Until the AppRouter types include it, every
 * access to those procedures routes through the one `unknown` cast below so the
 * rest of the notifications feature (bell, popover, inbox) stays fully typed.
 *
 * When the backend types land: delete the `notificationsApi` cast, swap the
 * hooks in `useNotifications.ts` to the real `trpc.notifications.*` proxy, and
 * delete this file. Mirrors the `pipeline-procedures.ts` precedent.
 *
 * The tRPC react proxy resolves procedure paths lazily, so these hooks are safe
 * to mount before the server procedure exists — only an actual network call
 * would 404, and that is gated behind the parallel backend landing.
 */
import { trpc } from '@/lib/trpc';

// ---- Contract types (mirror the parallel backend PR's IO) -----------------

export type NotificationType =
  | 'mention'
  | 'assigned'
  | 'record_update'
  | 'push_blocked'
  | 'system'
  | 'print_file_resolved'
  // BLU-665 — per-order 3PL↔client chat message. Lockstep with the backend
  // NotificationType union + the DB CHECK (20260719110000_order_messages_infra).
  | 'order_message';

export type NotificationFilter = 'unread' | 'all';

export interface NotificationActor {
  id?: string;
  name?: string;
}

export interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  /** Nullable — some events (a bare mention) have no body line. */
  body: string | null;
  /** In-app deep link. Null when the event has no navigable target. */
  link: string | null;
  actor?: NotificationActor | null;
  /** ISO timestamp; null while unread. */
  read_at: string | null;
  created_at: string;
}

export interface NotificationListPage {
  items: NotificationItem[];
  nextCursor?: string | null;
}

export interface NotificationListInput {
  filter: NotificationFilter;
  limit: number;
  /** Only supplied by the plain (popover) query; the infinite query lets tRPC
   *  inject the cursor from the page param. Optional here so both call sites
   *  share one input type. */
  cursor?: string | null;
}

// ---- Minimal hook shims ----------------------------------------------------

interface QueryOpts {
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number | false;
  refetchIntervalInBackground?: boolean;
  refetchOnWindowFocus?: boolean;
  placeholderData?: unknown;
}

interface QueryResult<O> {
  data: O | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: { message: string } | null;
  refetch: () => void;
}

type QueryHook<I, O> = (input: I, opts?: QueryOpts) => QueryResult<O>;
type VoidQueryHook<O> = (input?: undefined, opts?: QueryOpts) => QueryResult<O>;

interface InfiniteQueryOpts {
  getNextPageParam: (lastPage: NotificationListPage) => string | null | undefined;
  enabled?: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
}

interface InfiniteQueryResult {
  data: { pages: NotificationListPage[]; pageParams: unknown[] } | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: { message: string } | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

interface MutationOpts<I, O, Ctx> {
  onMutate?: (vars: I) => Ctx | Promise<Ctx>;
  onError?: (err: { message: string }, vars: I, ctx: Ctx | undefined) => void;
  onSuccess?: (data: O, vars: I) => void;
  onSettled?: (data: O | undefined, err: unknown, vars: I) => void;
}

interface MutationResult<I> {
  mutate: (input: I) => void;
  mutateAsync: (input: I) => Promise<unknown>;
  isPending: boolean;
}

type MutationHook<I, O> = <Ctx = unknown>(
  opts?: MutationOpts<I, O, Ctx>,
) => MutationResult<I>;

interface NotificationsApi {
  list: {
    useQuery: QueryHook<NotificationListInput, NotificationListPage>;
    useInfiniteQuery: (
      input: Omit<NotificationListInput, 'cursor'>,
      opts: InfiniteQueryOpts,
    ) => InfiniteQueryResult;
  };
  unreadCount: { useQuery: VoidQueryHook<number> };
  markRead: { useMutation: MutationHook<{ ids: string[] }, { ok: true }> };
  markAllRead: { useMutation: MutationHook<void, { ok: true }> };
  dismiss: { useMutation: MutationHook<{ id: string }, { ok: true }> };
  dismissAll: { useMutation: MutationHook<void, { ok: true }> };
}

/**
 * THE single cast. Every hook in `useNotifications.ts` reads through this.
 *
 * `notifications` isn't on the generated AppRouter type yet, so we route the
 * whole `trpc` proxy through `unknown` before narrowing — accessing an unknown
 * key on the typed proxy would otherwise be a compile error. The tRPC react
 * proxy resolves paths lazily at runtime, so `.notifications.*` is a valid live
 * handle the moment the backend router lands.
 */
export const notificationsApi = (trpc as unknown as { notifications: NotificationsApi })
  .notifications;

// ---- React-Query key prefixes ---------------------------------------------
//
// tRPC v11 query keys are shaped `[['notifications','<proc>'], { input, type }]`.
// Matching on the two-segment path prefix hits BOTH the plain popover query
// (`type: 'query'`) and the infinite inbox query (`type: 'infinite'`) in a
// single `setQueriesData`, so optimistic writes stay in sync everywhere the
// same notification is displayed.

export const NOTIFICATIONS_LIST_KEY = [['notifications', 'list']] as const;
export const NOTIFICATIONS_UNREAD_KEY = [['notifications', 'unreadCount']] as const;
